import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X } from 'lucide-react';
import Layout from '@/components/Layout';
import { BTS_VIDEOS } from '@/lib/index';
import { IMAGES } from '@/assets/images';

interface OnSetImage {
  src: string;
  caption: string;
}

const onSetImages: OnSetImage[] = [
  { src: IMAGES.KESHHAV_DIRECTOR_1_1_6, caption: 'Whispered Direction' },
  { src: IMAGES.KESHHAV_DIRECTOR_2_1_10, caption: 'Director\'s Perspective' },
  { src: IMAGES.KESHHAV_DIRECTOR_8_1_7, caption: 'Orchestrating Epic Scenes' },
  { src: IMAGES.KESHHAV_DIRECTOR_9_1_9, caption: 'Cinematic Vision' },
  { src: IMAGES.KESHHAV_DIRECTOR_10_1_14, caption: 'On-Set Mastery' },
  { src: IMAGES.KESHHAV_DIRECTOR_12_ZOOMIN_1_12, caption: 'Focused Direction' },
  { src: IMAGES.KESHHAV_DIRECTOR_13_1_13, caption: 'Commanding the Frame' },
  { src: IMAGES.KESHHAV_CAMERA_V3_1_8, caption: 'Behind the Lens' },
  { src: IMAGES.KESHHAV_ONSET_1_1_11, caption: 'Directorial Precision' },
];

export default function BehindTheScenes() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  const getVideoEmbedUrl = (platform: 'youtube' | 'vimeo', videoId: string) => {
    if (platform === 'youtube') {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return `https://player.vimeo.com/video/${videoId}`;
  };

  const getVideoThumbnail = (platform: 'youtube' | 'vimeo', videoId: string) => {
    if (platform === 'youtube') {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    return `https://vumbnail.com/${videoId}.jpg`;
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <section className="relative min-h-[40vh] md:min-h-[60vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.KESHHAV_DIRECTOR_12_16X9_1_15}
              alt="Behind the Camera"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>

          <div className="relative z-10 container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="font-display text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-foreground mb-4 md:mb-6">
                BEHIND THE CAMERA
              </h1>
              <p className="text-base md:text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto">
                Step inside the frame. Witness cinema being engineered.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4">
                VIDEO GALLERY
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Exclusive production footage from across the world
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {BTS_VIDEOS.map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group cursor-pointer"
                  onClick={() => setSelectedVideo(getVideoEmbedUrl(video.platform, video.videoId))}
                >
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-card shadow-cinematic">
                    <img
                      src={getVideoThumbnail(video.platform, video.videoId)}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-accent/90 flex items-center justify-center transform transition-all duration-300 group-hover:scale-110 shadow-gold">
                        <Play className="w-8 h-8 text-accent-foreground ml-1" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors duration-300">
                      {video.title}
                    </h3>
                    {video.description && (
                      <p className="text-sm text-muted-foreground mt-2">
                        {video.description}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-secondary/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4">
                DIRECTORIAL MASTERY ON SET
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Capturing the essence of cinematic creation
              </p>
            </motion.div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {onSetImages.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group cursor-pointer"
                  onClick={() => setSelectedImage(image.src)}
                >
                  <div className="relative aspect-[4/5] rounded-lg overflow-hidden bg-card shadow-cinematic">
                    <img
                      src={image.src}
                      alt={image.caption}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <p className="text-accent font-semibold text-lg">
                        {image.caption}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm p-4"
              onClick={() => setSelectedImage(null)}
            >
              <button
                className="absolute top-4 right-4 w-12 h-12 rounded-full bg-accent/90 flex items-center justify-center hover:bg-accent transition-colors shadow-gold"
                onClick={() => setSelectedImage(null)}
              >
                <X className="w-6 h-6 text-accent-foreground" />
              </button>
              <motion.img
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                src={selectedImage}
                alt="Full view"
                className="max-w-full max-h-full object-contain rounded-lg shadow-cinematic"
                onClick={(e) => e.stopPropagation()}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {selectedVideo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm p-4"
              onClick={() => setSelectedVideo(null)}
            >
              <button
                className="absolute top-4 right-4 w-12 h-12 rounded-full bg-accent/90 flex items-center justify-center hover:bg-accent transition-colors shadow-gold z-10"
                onClick={() => setSelectedVideo(null)}
              >
                <X className="w-6 h-6 text-accent-foreground" />
              </button>
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="w-full max-w-5xl aspect-video"
                onClick={(e) => e.stopPropagation()}
              >
                <iframe
                  src={selectedVideo}
                  className="w-full h-full rounded-lg shadow-cinematic"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}